import { fieldService, relationshipService } from '../services';
import { Asset } from './valueMap';
import { getHierarchyPath } from './formatData';
import { updateFieldsWithRelationships } from './updateFieldsWithRelationships';

/**
 * How a field is rendered where the "Not Null" pill appears. A nullable field says nothing — the
 * absence of the pill is the information — so only the constrained case carries a label.
 */
export const IsNullableValues = {
  true: { label: undefined, className: '' },
  false: { label: 'NN', className: 'pill pill-grey pill-narrow', tooltip: 'Not Null' },
};

/**
 * Shapes a field as a row of the dataset tree, aliased onto the dataset_* display keys.
 *
 * A field is a row of the same table its dataset is, so its checkbox, its sensitivity and its status
 * land in the same columns as the datasets above it. What a field has and a dataset does not — its
 * datatype, its key, whether it is nullable — rides along in the Name column as inline entries, which
 * is where a field's own list shows them too.
 *
 * A field's place in the tree is its parent field when it has one (the fields of a JSON response
 * nest), and its dataset when it does not.
 *
 * @param {object} field - Row from GET /field/all.
 * @returns {object}
 */
export const toFieldRow = field => ({
  ...field,
  unique_id: `${Asset.Field}-${field.field_id}`,
  parent_unique_id: field.parent_field_id
    ? `${Asset.Field}-${field.parent_field_id}`
    : `${Asset.Dataset}-${field.dataset_id}`,
  object_id: field.field_id,
  object_name: Asset.Field,
  link: `/systems/${field.system_id}/${field.datasource_id}/${field.dataset_group_id}/${field.dataset_id}/${field.field_id}`,
  dataset_name: field.field_name,
  dataset_description: field.field_description,
  pii: field.is_pii ? 'PII' : null,
  pii_tooltip: field.is_pii ? 'Classified as Personally Identifiable Information (PII)' : null,
  // Whether it is a key is only fully known once the dataset's relationships are read, which happens
  // when a dataset is expanded — updateFieldsWithRelationships works it out and its answer stands. A
  // field arriving from a search across the group has no relationships to go on, so its own flag
  // stands in: the primary key it declares is right, and a foreign key shows no icon rather than a
  // wrong one.
  key_type: field.key_type ?? (field.is_primary_key ? 'Primary Key' : null),
  key_list: field.key_list ?? (field.is_primary_key ? 'Primary Key' : undefined),
  // The text of the "Not Null" pill. An inline pill renders what a property holds and styles it from
  // its value map, so the constraint needs a property of its own to say; a nullable field says
  // nothing, which is the absence of the pill.
  not_null: field.is_nullable === false ? IsNullableValues.false.label : undefined,
});

/**
 * How a filterable column of the dataset tree maps onto the field that stands in for it.
 *
 * The tree's columns are the dataset ones and a field is aliased onto them, so one tick in the
 * filter's column selector covers both kinds of row — and a server-side search of the fields has to
 * translate that tick back. Datatype is a field's own, ticked under its own name, since only fields
 * have one. A column with no entry here is not searched: the description is left out because a
 * field's resolved description is assembled from several columns rather than being one of them.
 */
const FIELD_COLUMN_FOR_TREE_COLUMN = {
  dataset_name: 'field_name',
  datatype_name: 'datatype_name',
  attribute_sensitivity_name: 'attribute_sensitivity_name',
};

/**
 * Gives each field row the ancestry line its tooltip shows, walked over the list it is joining.
 *
 * A field's chain runs through the fields above it — an API response nests them — and on into its
 * dataset and the datasets above that, all of which are already on screen.
 *
 * @param {object[]} fields - Shaped field rows
 * @param {object[]} datasetRows - The list's current dataset rows
 * @returns {object[]} The same rows, each with dataset_path
 */
export const withFieldPaths = (fields, datasetRows) => {
  // Keyed for the walk rather than scanned per level: a dataset's fields against a group of
  // thousands of rows would otherwise be a scan per field per level.
  const byId = new Map([...(datasetRows || []), ...fields].map(row => [row.unique_id, row]));

  return fields.map(field => ({
    ...field,
    dataset_path: getHierarchyPath(field, byId, Asset.Dataset, ' > '),
  }));
};

/**
 * Loads the fields of one or more datasets — one row opening, or every open row being re-read after
 * a change.
 *
 * A list rather than an id because re-reading is the common case and asking per dataset is a request
 * per open row. The fields of every dataset asked for come back in one `in` clause, which the API
 * binds as a single array parameter. What bounds it is the URL: an id list of a few thousand
 * outgrows the query string, and a POST endpoint is the answer if that is ever reached.
 *
 * The relationships come in one read too — they are what says which fields are keys. They are not
 * split back up per dataset, and do not need to be: a relationship is matched to a field by field
 * id, so the whole set resolves against one dataset's fields exactly as that dataset's own subset
 * would.
 *
 * Every field of a dataset is read, not only its top-level ones: the fields of a JSON response nest
 * into each other, and reading the whole dataset at once means opening one of those costs no further
 * request. A dataset holds a bounded number of fields — it is the datasource that can hold tens of
 * thousands.
 *
 * @param {object} args
 * @param {Array<number|string>} args.datasetIds
 * @param {AbortSignal} [args.signal]
 * @returns {Promise<object[]>} Tree rows of every dataset asked for, each placed under its parent
 *   field or its dataset.
 */
export const fetchDatasetFields = async ({ datasetIds, signal } = {}) => {
  const ids = (datasetIds || []).map(Number).filter(id => !isNaN(id));

  // Nothing to ask for. Without this the clause would be an empty `in ()`, which is a 400.
  if (ids.length === 0) return [];

  const query = new URLSearchParams({ $filter: `dataset_id in (${ids.join(',')})` });

  const [fieldResult, relationshipResult] = await Promise.all([
    fieldService.getAll(query.toString(), signal),
    // Datasets without relationships are the normal case for a file or API datasource, and a failed
    // read is not a reason to fail the expansion: the fields are still worth showing without their
    // key icons.
    relationshipService.getByDatasets(ids, signal).catch(() => ({})),
  ]);

  const relationships = relationshipResult?.relationships || [];

  // Which fields belong to which dataset. The key pass below is per dataset — a composite primary
  // key spans one dataset's fields, and the display order it produces is that dataset's — so the
  // fields have to be split back up before it runs. The relationships do not.
  const fieldsByDataset = new Map(ids.map(id => [id, []]));
  (fieldResult?.fields || []).forEach(field => {
    fieldsByDataset.get(Number(field.dataset_id))?.push(field);
  });

  return ids.flatMap(id =>
    updateFieldsWithRelationships({
      fields: fieldsByDataset.get(id) || [],
      relationships,
    }).map(toFieldRow)
  );
};

/**
 * Searches fields across a whole tree of datasets, for the filter box.
 *
 * A dataset's fields are only read when it is expanded, so a filter cannot match them in the
 * browser — this is the request that does it instead. `scope` is what confines the search to the
 * datasets the table is showing, since the endpoint sees every field in the installation: a whole
 * group in the dataset group's own list, and the datasets on screen when the list is one dataset's
 * subtree. A match outside it would have no row to appear under.
 *
 * @param {object} args
 * @param {string} args.scope - A $filter expression naming the tree, e.g. `dataset_group_id eq 51`.
 * @param {string} args.term - What the user typed.
 * @param {string[]} args.fields - The tree columns the filter's column selector has ticked.
 * @param {number} [args.top] - Maximum number of matches to ask for.
 * @param {AbortSignal} [args.signal]
 * @returns {Promise<object[]>} Tree rows, each placed under its parent field or its dataset.
 */
export const searchDatasetFields = async ({ scope, term, fields, top, signal } = {}) => {
  const columns = [
    ...new Set(
      (fields || []).map(field => FIELD_COLUMN_FOR_TREE_COLUMN[field]).filter(column => column)
    ),
  ];

  // Nothing searchable was asked for, so there is nothing to ask the server.
  if (!scope || !term || columns.length === 0) return [];

  // OData escapes a quote inside a string literal by doubling it. Case-insensitively, via tolower(),
  // because the filter box has always matched that way.
  const literal = String(term).toLowerCase().replaceAll("'", "''");
  const matches = columns.map(column => `contains(tolower(${column}),'${literal}')`).join(' or ');

  const query = new URLSearchParams({ $filter: `${scope} and (${matches})` });
  if (top) query.set('$top', top);

  return fieldService
    .getAll(query.toString(), signal)
    .then(res => _placeOrphans((res?.fields || []).map(toFieldRow)));
};

/**
 * Re-roots a matched field onto its dataset when the field it belongs under is not among the matches.
 *
 * The fields of an API response nest, and a search finds the field that matched, not the ones above
 * it. A row whose parent is nowhere in the table is not placed at all — it would simply not appear —
 * so it is attached to its dataset instead, which is present because every dataset is. Once its
 * dataset is opened the loaded row takes over, with its real place in the tree.
 *
 * @param {object[]} rows - Matched field rows
 * @returns {object[]} The same rows, each with a parent that exists
 */
const _placeOrphans = rows => {
  const matched = new Set(rows.map(row => row.unique_id));

  return rows.map(row =>
    row.parent_field_id && !matched.has(`${Asset.Field}-${row.parent_field_id}`)
      ? { ...row, parent_unique_id: `${Asset.Dataset}-${row.dataset_id}` }
      : row
  );
};
